/**
 * Pure rules for the company chart of accounts. No database access here.
 */
import { accountClassOf, classInfo, findBasAccount, isFreeBasNumber, isValidAccountNumber } from '../bas';
import type { AccountClass } from '../types';

export type AccountRuleError =
  | { code: 'invalid_number'; message: string }
  | { code: 'not_in_bas'; message: string }
  | { code: 'not_free'; message: string }
  | { code: 'wrong_class'; message: string }
  | { code: 'missing_sru'; message: string }
  | { code: 'missing_name'; message: string }
  | { code: 'already_exists'; message: string };

export type RuleResult = { ok: true } | { ok: false; errors: AccountRuleError[] };

export interface CustomAccountInput {
  number: number;
  name: string;
  sruCode: string;
  /** The class the user selected; must match the number's class. */
  accountClass: AccountClass;
}

/** SRU codes are four-digit numeric field codes. */
export const SRU_CODE_PATTERN = /^\d{4}$/;

export function validateCustomAccount(input: CustomAccountInput, existingNumbers: ReadonlySet<number>): RuleResult {
  const errors: AccountRuleError[] = [];

  if (!isValidAccountNumber(input.number)) {
    errors.push({ code: 'invalid_number', message: 'Kontonummer måste vara mellan 1000 och 8999' });
    return { ok: false, errors };
  }
  if (!isFreeBasNumber(input.number)) {
    errors.push({ code: 'not_free', message: 'Kontonumret finns redan i BAS-kontoplanen. Aktivera det i stället.' });
  }
  const actualClass = accountClassOf(input.number);
  if (actualClass !== input.accountClass) {
    const info = classInfo(input.accountClass);
    errors.push({
      code: 'wrong_class',
      message: `Kontonumret tillhör klass ${actualClass}, inte klass ${input.accountClass} (${info.min}–${info.max})`,
    });
  }
  if (!SRU_CODE_PATTERN.test(input.sruCode.trim())) {
    errors.push({ code: 'missing_sru', message: 'SRU-kod (fyra siffror) krävs för egna konton' });
  }
  if (!input.name.trim()) {
    errors.push({ code: 'missing_name', message: 'Kontonamn krävs' });
  }
  if (existingNumbers.has(input.number)) {
    errors.push({ code: 'already_exists', message: 'Kontot finns redan i företagets kontoplan' });
  }
  return errors.length ? { ok: false, errors } : { ok: true };
}

export function validateBasActivation(number: number, existingNumbers: ReadonlySet<number>): RuleResult {
  const errors: AccountRuleError[] = [];
  if (!isValidAccountNumber(number)) {
    errors.push({ code: 'invalid_number', message: 'Kontonummer måste vara mellan 1000 och 8999' });
  } else if (!findBasAccount(number)) {
    errors.push({ code: 'not_in_bas', message: 'Kontot finns inte i BAS-kontoplanen' });
  }
  if (existingNumbers.has(number)) {
    errors.push({ code: 'already_exists', message: 'Kontot är redan aktiverat' });
  }
  return errors.length ? { ok: false, errors } : { ok: true };
}
